/**
 * Initializes App Config Context that gives all components access to the
 * App Config object from the useAppConfig hook.
 *
 * Also responsible for initializing any upfront services needed and pulling in env-vars
 * created by CI Pipeline.
 *
 * @see https://chalk.charter.com/display/ERSPM/FE+Configs.
 */

import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KiteProgressIndicator } from '@kite/react-kite';
import * as datadog from './services/datadogService';
import { createAppConfig } from '../config/appConfig/appConfig';
import { fetchEnvConfig } from '../config/envConfig/envConfig';
import MockService from './services/mockService';
import appConfigDefault from './utils/appConfigDefault';
import { IAppConfig } from '../config/appConfig/appConfig.types';
import { IEnvConfig } from '../config/envConfig/envConfig.types';
import QuantumService from './services/quantumService';

interface IAppConfigContext {
  appConfig: IAppConfig;
}

export const AppConfigContext = createContext<IAppConfigContext>({
  appConfig: appConfigDefault,
});

export const AppConfigContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [appConfig, setAppConfig] = useState<IAppConfig>();

  const { data, isSuccess } = useQuery<IEnvConfig, Error>({
    queryKey: ['envConfig'],
    queryFn: fetchEnvConfig,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const config = createAppConfig(data);
      if (!config.isProd) {
        console.debug('App Config', config);
      }
      datadog.init(config);
      MockService.init(config)
        .then(() => {
          return QuantumService.init(config)
            .finally(() => {
              setAppConfig(config);
            })
            .catch(console.error);
        })
        .catch(console.error);
    }
  }, [isSuccess, data]);

  const contextValue = useMemo(() => ({ appConfig: appConfig! }), [appConfig]);
  return (
    <AppConfigContext.Provider value={contextValue}>
      {appConfig && children}
      {!appConfig && (
        <div className="display-loader">
          <KiteProgressIndicator id="app-config-loader" />
        </div>
      )}
    </AppConfigContext.Provider>
  );
};
